/**
 * Shared store for the Firefly `user` object (MPMUser in MPM's extended schema).
 *
 * Analytics hooks are used throughout the app, but job details arrive from the
 * user details API and the selected location lives in UserContext. App.tsx
 * writes both here, and every event builder reads them when the event is sent.
 *
 * Only job/role data and the location id are sent. Names and emails must not be
 * added: Firefly data is readable by all internal users.
 */

export interface JobDetails {
    department?: string
    job_code?: string
    job_code_description?: string
    job_location_type?: string
    job_title?: string
}

export interface FireflyUserInfo extends JobDetails {
    location_id?: number
}

let jobDetails: JobDetails | undefined
let selectedLocationId: number | undefined

export function setAnalyticsJobDetails(details: JobDetails | undefined) {
    jobDetails = details
}

export function setAnalyticsLocationId(
    locationId: string | number | undefined,
) {
    const trimmed = String(locationId ?? '').trim()
    const parsed = Number(trimmed)
    selectedLocationId =
        trimmed !== '' && Number.isInteger(parsed) ? parsed : undefined
}

export function resetAnalyticsUserInfo() {
    jobDetails = undefined
    selectedLocationId = undefined
}

const nonEmpty = (value: unknown): value is string =>
    typeof value === 'string' && value.trim() !== ''

export function buildUserInfo(): FireflyUserInfo | undefined {
    const user: FireflyUserInfo = {}

    if (jobDetails) {
        const fields: (keyof JobDetails)[] = [
            'department',
            'job_code',
            'job_code_description',
            'job_location_type',
            'job_title',
        ]
        fields.forEach((field) => {
            const value = jobDetails?.[field]
            if (nonEmpty(value)) {
                user[field] = value.trim()
            }
        })
    }

    if (selectedLocationId !== undefined) {
        user.location_id = selectedLocationId
    }

    return Object.keys(user).length > 0 ? user : undefined
}

export function withUserInfo<T extends object>(
    payload: T,
): T & { user?: FireflyUserInfo } {
    const user = buildUserInfo()
    return user ? { ...payload, user } : payload
}
